import { useState } from 'react'
import { Button, Switch } from '@/shared/ui'
import {
  useAdminCampaignAction,
  useAdminImpersonate,
  useAdminResetPassword,
  useAdminUpdateVoice,
} from '@/entities/admin'
import { toast } from '@/shared/model/toast'
import type {
  AdminCampaignSummary,
  AdminVoicePatch,
  ModelChoice,
  User,
  VoiceSettings,
} from '@/shared/api/types'
import { AdminRemoteState } from '../shared/AdminRemoteState'
import {
  campaignStatusLabel,
  formatAdminDate,
  formatNumber,
} from '../shared/format'
import styles from '../AdminPages.module.css'

export function CompanyCampaignsTab({
  campaigns,
}: {
  campaigns: AdminCampaignSummary[]
}) {
  const control = useAdminCampaignAction()

  function runAction(campaign: AdminCampaignSummary, action: 'pause' | 'stop') {
    const warning =
      action === 'pause'
        ? `Приостановить обзвон «${campaign.name}»? Новые звонки не начнутся, пока клиент не продолжит обзвон.`
        : `Остановить обзвон «${campaign.name}»? Это действие нельзя отменить.`
    if (window.confirm(warning)) control.mutate({ campaignId: campaign.id, action })
  }

  if (campaigns.length === 0) {
    return (
      <AdminRemoteState
        kind="empty"
        title="У компании нет обзвонов"
        message="Запуски появятся здесь после создания первого обзвона."
      />
    )
  }

  return (
    <>
      <div className={`${styles.tableWrap} ${styles.desktopTable}`}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Обзвон</th>
              <th>Состояние</th>
              <th>Создан</th>
              <th>Завершён</th>
              <th>Ход работы</th>
              <th>В очереди</th>
              <th>Ошибки</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign) => {
              const progress = campaignProgress(campaign)
              return (
                <tr key={campaign.id}>
                  <td>
                    <strong>{campaign.name}</strong>
                    <div className={styles.muted}>№ {campaign.id}</div>
                  </td>
                  <td>
                    <CampaignStatus campaign={campaign} />
                  </td>
                  <td>{formatAdminDate(campaign.createdAt)}</td>
                  <td>{formatAdminDate(campaign.finishedAt)}</td>
                  <td>
                    <CampaignProgress campaign={campaign} progress={progress} />
                  </td>
                  <td>{formatNumber(campaign.queued)}</td>
                  <td>{formatNumber(campaign.failed)}</td>
                  <td>
                    <CampaignActions
                      campaign={campaign}
                      pending={control.isPending}
                      onAction={runAction}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className={styles.mobileCards} aria-label="Список обзвонов">
        {campaigns.map((campaign) => (
          <CampaignMobileCard
            key={campaign.id}
            campaign={campaign}
            pending={control.isPending}
            onAction={runAction}
          />
        ))}
      </div>
    </>
  )
}

function CampaignMobileCard({
  campaign,
  pending,
  onAction,
}: {
  campaign: AdminCampaignSummary
  pending: boolean
  onAction: (campaign: AdminCampaignSummary, action: 'pause' | 'stop') => void
}) {
  const progress = campaignProgress(campaign)

  return (
    <article className={styles.mobileCard}>
      <OperationMobileRow
        label="Обзвон"
        value={
          <>
            <strong>{campaign.name}</strong>
            <div className={styles.muted}>№ {campaign.id}</div>
          </>
        }
      />
      <OperationMobileRow label="Состояние" value={<CampaignStatus campaign={campaign} />} />
      <OperationMobileRow label="Создан" value={formatAdminDate(campaign.createdAt)} />
      <OperationMobileRow label="Завершён" value={formatAdminDate(campaign.finishedAt)} />
      <OperationMobileRow
        label="Ход работы"
        value={<CampaignProgress campaign={campaign} progress={progress} mobile />}
      />
      <OperationMobileRow label="В очереди" value={formatNumber(campaign.queued)} />
      <OperationMobileRow label="Ошибки" value={formatNumber(campaign.failed)} />
      <CampaignActions campaign={campaign} pending={pending} onAction={onAction} mobile />
    </article>
  )
}

function CampaignStatus({ campaign }: { campaign: AdminCampaignSummary }) {
  const running = campaign.status === 'running'
  return (
    <span
      className={`${styles.status} ${running ? styles.statusGood : campaign.status === 'paused' ? styles.statusWarn : ''}`}
    >
      {campaignStatusLabel(campaign.status)}
    </span>
  )
}

function CampaignProgress({
  campaign,
  progress,
  mobile = false,
}: {
  campaign: AdminCampaignSummary
  progress: number
  mobile?: boolean
}) {
  return (
    <>
      <div
        className={`${styles.progress} ${mobile ? styles.mobileProgress : ''}`}
        title={`Выполнено ${progress}%`}
      >
        <div className={styles.progressBar} style={{ width: `${Math.min(100, progress)}%` }} />
      </div>
      <span className={styles.muted}>
        {formatNumber(campaign.done)} из {formatNumber(campaign.total)}
      </span>
    </>
  )
}

function CampaignActions({
  campaign,
  pending,
  onAction,
  mobile = false,
}: {
  campaign: AdminCampaignSummary
  pending: boolean
  onAction: (campaign: AdminCampaignSummary, action: 'pause' | 'stop') => void
  mobile?: boolean
}) {
  const running = campaign.status === 'running'
  const canStop = running || campaign.status === 'paused'

  return (
    <div
      className={`${styles.tableActions} ${mobile ? styles.mobileCardActions : ''}`}
      aria-label={mobile ? `Действия с обзвоном «${campaign.name}»` : undefined}
    >
      {running && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onAction(campaign, 'pause')}
          disabled={pending}
        >
          Приостановить
        </Button>
      )}
      {canStop && (
        <Button
          variant="danger"
          size="sm"
          onClick={() => onAction(campaign, 'stop')}
          disabled={pending}
        >
          Остановить
        </Button>
      )}
      {!running && !canStop && <span className={styles.muted}>Нет действий</span>}
    </div>
  )
}

function campaignProgress(campaign: AdminCampaignSummary): number {
  return campaign.total > 0 ? Math.round((campaign.done / campaign.total) * 100) : 0
}

interface TemporaryPassword {
  userId: number
  value: string
}

export function CompanyUsersTab({ companyName, users }: { companyName: string; users: User[] }) {
  const resetPassword = useAdminResetPassword()
  const impersonate = useAdminImpersonate()
  const [temporaryPassword, setTemporaryPassword] = useState<TemporaryPassword | null>(null)

  function reset(user: User) {
    const confirmed = window.confirm(
      `Сбросить пароль пользователя ${user.email}? Старый пароль перестанет работать, а временный будет показан только сейчас.`,
    )
    if (!confirmed) return
    resetPassword.mutate(user.id, {
      onSuccess: (result) => setTemporaryPassword({ userId: user.id, value: result.temporaryPassword }),
    })
  }

  function enter(user: User) {
    const confirmed = window.confirm(
      `Войти как ${user.email} в компанию «${companyName}»? Все действия будут выполняться от имени этого пользователя.`,
    )
    if (confirmed) impersonate.mutate(user.id)
  }

  async function copyPassword(value: string) {
    try {
      await navigator.clipboard.writeText(value)
      toast.success('Временный пароль скопирован')
    } catch {
      toast.error('Не удалось скопировать пароль')
    }
  }

  if (users.length === 0) {
    return (
      <AdminRemoteState
        kind="empty"
        title="У компании нет пользователей"
        message="Пользователи появятся после приглашения или регистрации."
      />
    )
  }

  return (
    <>
      <div className={`${styles.tableWrap} ${styles.desktopTable}`}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Пользователь</th>
              <th>Роль</th>
              <th>Создан</th>
              <th>Последний вход</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td className={styles.mono}>{user.id}</td>
                <td>
                  <strong>{user.name || 'Без имени'}</strong>
                  <div className={styles.muted}>{user.email}</div>
                  {temporaryPassword?.userId === user.id && (
                    <TemporaryPasswordBox
                      value={temporaryPassword.value}
                      onCopy={copyPassword}
                    />
                  )}
                </td>
                <td>{userRoleLabel(user.role)}</td>
                <td>{formatAdminDate(user.createdAt)}</td>
                <td>{formatAdminDate(user.lastLoginAt)}</td>
                <td>
                  <UserActions
                    user={user}
                    pending={resetPassword.isPending || impersonate.isPending}
                    onReset={reset}
                    onEnter={enter}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.mobileCards} aria-label="Список пользователей">
        {users.map((user) => (
          <UserMobileCard
            key={user.id}
            user={user}
            temporaryPassword={temporaryPassword?.userId === user.id ? temporaryPassword.value : null}
            pending={resetPassword.isPending || impersonate.isPending}
            onReset={reset}
            onEnter={enter}
            onCopy={copyPassword}
          />
        ))}
      </div>
    </>
  )
}

function UserMobileCard({
  user,
  temporaryPassword,
  pending,
  onReset,
  onEnter,
  onCopy,
}: {
  user: User
  temporaryPassword: string | null
  pending: boolean
  onReset: (user: User) => void
  onEnter: (user: User) => void
  onCopy: (value: string) => Promise<void>
}) {
  return (
    <article className={styles.mobileCard}>
      <OperationMobileRow label="ID" value={String(user.id)} mono />
      <OperationMobileRow
        label="Пользователь"
        value={
          <>
            <strong>{user.name || 'Без имени'}</strong>
            <div className={styles.muted}>{user.email}</div>
          </>
        }
      />
      <OperationMobileRow label="Роль" value={userRoleLabel(user.role)} />
      <OperationMobileRow label="Создан" value={formatAdminDate(user.createdAt)} />
      <OperationMobileRow label="Последний вход" value={formatAdminDate(user.lastLoginAt)} />
      {temporaryPassword && <TemporaryPasswordBox value={temporaryPassword} onCopy={onCopy} />}
      <UserActions
        user={user}
        pending={pending}
        onReset={onReset}
        onEnter={onEnter}
        mobile
      />
    </article>
  )
}

function TemporaryPasswordBox({
  value,
  onCopy,
}: {
  value: string
  onCopy: (value: string) => Promise<void>
}) {
  return (
    <div className={styles.passwordBox} role="status">
      <div className={styles.passwordContent}>
        <div className={styles.label}>Временный пароль</div>
        <div className={styles.passwordValue}>{value}</div>
      </div>
      <Button variant="secondary" size="sm" onClick={() => void onCopy(value)}>
        Копировать
      </Button>
    </div>
  )
}

function UserActions({
  user,
  pending,
  onReset,
  onEnter,
  mobile = false,
}: {
  user: User
  pending: boolean
  onReset: (user: User) => void
  onEnter: (user: User) => void
  mobile?: boolean
}) {
  return (
    <div
      className={`${styles.tableActions} ${mobile ? styles.mobileCardActions : ''}`}
      aria-label={mobile ? `Действия с пользователем ${user.email}` : undefined}
    >
      <Button
        variant="secondary"
        size="sm"
        onClick={() => onReset(user)}
        disabled={pending}
      >
        Сбросить пароль
      </Button>
      <Button variant="brand" size="sm" onClick={() => onEnter(user)} disabled={pending}>
        Войти как пользователь
      </Button>
    </div>
  )
}

function OperationMobileRow({
  label,
  value,
  mono = false,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
}) {
  return (
    <div className={styles.mobileRow}>
      <span className={styles.mobileLabel}>{label}</span>
      <div className={`${styles.mobileValue} ${mono ? styles.mono : ''}`} data-admin-value>
        {value}
      </div>
    </div>
  )
}

interface VoiceDraft {
  ttsModel: string
  voiceId: string
  llmModel: string
  speed: number
  neuralVad: boolean
}

export function CompanyVoiceTab({ companyId, voice }: { companyId: number; voice: VoiceSettings }) {
  const update = useAdminUpdateVoice(companyId)
  const initial: VoiceDraft = {
    ttsModel: voice.ttsModel,
    voiceId: voice.voiceId,
    llmModel: voice.llmModel,
    speed: voice.speed,
    neuralVad: voice.neuralVad,
  }
  const [draft, setDraft] = useState<VoiceDraft>(initial)
  const speedValid = Number.isFinite(draft.speed) && draft.speed >= 0.5 && draft.speed <= 1.5
  const dirty =
    draft.ttsModel !== initial.ttsModel ||
    draft.voiceId !== initial.voiceId ||
    draft.llmModel !== initial.llmModel ||
    draft.speed !== initial.speed ||
    draft.neuralVad !== initial.neuralVad

  function save() {
    if (!dirty || !speedValid) return
    if (!window.confirm('Вы меняете прод-настройки клиента. Сохранить изменения?')) return

    const patch: AdminVoicePatch = {}
    if (draft.ttsModel !== initial.ttsModel) patch.ttsModel = draft.ttsModel
    if (draft.voiceId !== initial.voiceId) patch.voiceId = draft.voiceId
    if (draft.llmModel !== initial.llmModel) patch.llmModel = draft.llmModel
    if (draft.speed !== initial.speed) patch.speed = draft.speed
    if (draft.neuralVad !== initial.neuralVad) patch.neuralVad = draft.neuralVad
    update.mutate(patch)
  }

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <div>
          <h2 className={styles.panelTitle}>Рабочие настройки голоса</h2>
          <p className={styles.panelHint}>Изменения применятся к следующим звонкам клиента.</p>
        </div>
        <span className={styles.statusWarn + ' ' + styles.status}>Прод-настройки</span>
      </div>

      <div className={styles.formGrid}>
        <label className={styles.field}>
          <span className={styles.label}>Модель синтеза речи</span>
          <select
            className={styles.select}
            value={draft.ttsModel}
            onChange={(event) => setDraft((current) => ({ ...current, ttsModel: event.target.value }))}
          >
            {modelOptions(voice.ttsChoices, draft.ttsModel).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Диалоговая модель</span>
          <select
            className={styles.select}
            value={draft.llmModel}
            onChange={(event) => setDraft((current) => ({ ...current, llmModel: event.target.value }))}
          >
            {modelOptions(voice.llmChoices, draft.llmModel).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Идентификатор голоса</span>
          <input
            className={styles.input}
            value={draft.voiceId}
            onChange={(event) => setDraft((current) => ({ ...current, voiceId: event.target.value }))}
            placeholder="По умолчанию"
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Темп речи</span>
          <input
            className={styles.input}
            type="number"
            min="0.5"
            max="1.5"
            step="0.05"
            value={draft.speed}
            aria-invalid={!speedValid}
            onChange={(event) =>
              setDraft((current) => ({ ...current, speed: Number(event.target.value) }))
            }
          />
          {!speedValid && <span className={styles.errorText}>Укажите темп от 0,5 до 1,5.</span>}
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Детектор речи Silero (нейросеть)</span>
          <Switch
            checked={draft.neuralVad}
            onChange={(event) => setDraft((current) => ({ ...current, neuralVad: event.target.checked }))}
            label={draft.neuralVad ? 'включён' : 'выключен — детектор по громкости'}
          />
          <span className={styles.panelHint}>
            Когда человек начал и закончил говорить, решает нейросеть, а не громкость звука. Лучше слышит тихую и
            протяжную речь, не путает шум линии с голосом. Экспериментально: включайте по одной компании и слушайте
            записи.
          </span>
        </label>
      </div>

      <div className={styles.formActions}>
        <Button
          variant="secondary"
          onClick={() => setDraft(initial)}
          disabled={!dirty || update.isPending}
        >
          Отменить правки
        </Button>
        <Button onClick={save} disabled={!dirty || !speedValid} loading={update.isPending}>
          Сохранить настройки
        </Button>
      </div>
    </section>
  )
}

function modelOptions(choices: ModelChoice[], current: string) {
  const options = [
    { value: '', label: 'Как настроено по умолчанию' },
    ...choices.map((choice) => ({
      value: choice.model,
      label: choice.note ? `${choice.label} — ${choice.note}` : choice.label,
    })),
  ]
  if (current && !options.some((option) => option.value === current)) {
    options.push({ value: current, label: `${current} — текущее значение` })
  }
  return options
}

function userRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    owner: 'Владелец',
    admin: 'Администратор',
    manager: 'Менеджер',
    user: 'Пользователь',
  }
  return labels[role] ?? 'Пользователь'
}
